import { motion, AnimatePresence, useDragControls, type PanInfo } from 'motion/react'
import { SheetGrip } from '../SheetGrip'
import { X, Camera, MessageCircle, ImageIcon, Lightbulb } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useTelegram } from '../../hooks/useTelegram'

const F = 'Roboto, sans-serif'

interface Props {
  open: boolean
  onClose: () => void
  /** Username of the conversation bot (for opening curator chat). */
  botUsername?: string | null
}

// ── Athletic figure silhouettes (front / side / back) ──
// Flat-fill silhouettes with feminine proportions: small head, broad shoulders,
// narrow waist, defined hips. Sports bra (top half) + leggings (bottom half),
// matching the user reference's purple palette. No facial details — pose
// recognisability comes from silhouette shape alone.
function PoseFigure({ label, view }: { label: string; view: 'front' | 'side' | 'back' }) {
  // Фото-пример нужной позы (анфас / сбоку, руки вперёд / сзади). Файлы в
  // public/photo-guide/. onError прячет картинку, если файл ещё не залит.
  return (
    <div style={{
      flex: 1,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: 8,
      padding: '10px 4px 8px',
      background: 'linear-gradient(180deg, rgba(108,92,231,0.06) 0%, rgba(255,255,255,0.85) 100%)',
      border: '1px solid rgba(108,92,231,0.22)',
      borderRadius: 16,
      fontFamily: F,
    }}>
      <img
        src={`${import.meta.env.BASE_URL}photo-guide/${view}.png`}
        alt={label}
        loading="lazy"
        onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none' }}
        style={{ width: '100%', maxWidth: 92, height: 150, objectFit: 'contain', display: 'block' }}
      />
      <span style={{ fontSize: 12, fontWeight: 700, color: '#1C2D41' }}>{label}</span>
    </div>
  )
}

const RULES: { icon: string; title: string; text: string }[] = [
  { icon: '📷', title: 'Штатив или опора', text: 'Поставь телефон на штатив или подопри (стол, кружка). Запомни ракурс — он должен быть одинаковым каждый раз.' },
  { icon: '🌤️', title: 'Естественное освещение', text: 'Свет от окна, желательно днём. Искусственный сверху-спереди скрывает рельеф — фото будет необъективным.' },
  { icon: '👙', title: 'Облегающая одежда не нужна', text: 'Купальник или нижнее бельё. Допустимо: короткие НЕобтягивающие шорты + короткий топ. НЕ костюм аквалангиста, не лосины.' },
  { icon: '📸', title: '3 ракурса', text: 'Спереди · Сбоку (руки вытянуть вперёд) · Сзади. Снимай в полный рост, без обрезов.' },
  { icon: '🎬', title: 'Бонус — видео в 4K', text: 'Сними короткое видео в хорошем качестве — потом удобно нарезать кадры и сделать ролик трансформации.' },
  { icon: '🚫', title: 'Без селфи в зеркале', text: 'Зеркальные селфи искажают пропорции и не отражают реальный вид.' },
]

export function PhotoReportSheet({ open, onClose, botUsername }: Props) {
  const navigate = useNavigate()
  const { openTelegramLink, close } = useTelegram()
  const curatorChatUrl = botUsername ? `https://t.me/${botUsername}` : null
  // Drag-to-close активируется только с верхней «ручки» — внутри body работает
  // нативный scroll (иначе framer-motion перехватывал свайпы и нельзя было
  // долистать инструкцию).
  const dragControls = useDragControls()

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    if (info.offset.y > 80) onClose()
  }

  const handleOpenChat = () => {
    if (!curatorChatUrl) return
    // Внутри Telegram openTelegramLink открывает чат с ботом и закрывает Mini App.
    // Вне Telegram (dev/браузер) — фоллбэк на window.open.
    openTelegramLink(curatorChatUrl)
    close()
  }

  const handleOpenHistory = () => {
    onClose()
    navigate('/progress-photos')
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0, zIndex: 410,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
            fontFamily: F,
          }}
        >
          <motion.div
            data-testid="photo-report-sheet"
            initial={{ y: '100%' }} animate={{ y: 0 }} exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            drag="y"
            dragControls={dragControls}
            dragListener={false}
            dragConstraints={{ top: 0 }}
            dragElastic={0.1}
            onDragEnd={handleDragEnd}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#F5F6FA',
              borderTopLeftRadius: 28, borderTopRightRadius: 28,
              paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
              maxHeight: '92vh',
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Pull-handle area — drag only starts from here */}
            <div
              onPointerDown={(e) => dragControls.start(e)}
              style={{
                display: 'flex', justifyContent: 'center',
                paddingTop: 10, paddingBottom: 8,
                touchAction: 'none',
                cursor: 'grab',
                flexShrink: 0,
              }}
            >
              <SheetGrip onClose={onClose} />
            </div>

            {/* Scrollable body */}
            <div style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>

            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', padding: '12px 20px 8px', gap: 12 }}>
              <div style={{
                width: 44, height: 44, borderRadius: 14,
                background: 'rgba(32,201,151,0.14)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Camera size={22} color="#20C997" />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#1C2D41' }}>Фото прогресса</div>
                <div style={{ fontSize: 12, color: '#9AA5B4', marginTop: 1 }}>Раз в 30 дней · сдаётся в Telegram</div>
              </div>
              <button
                data-testid="photo-report-close"
                onClick={onClose}
                style={{
                  width: 32, height: 32, borderRadius: '50%',
                  border: 'none', background: 'rgba(0,0,0,0.07)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                }}
              >
                <X size={16} color="#6B7B8D" />
              </button>
            </div>

            {/* Schematic poses */}
            <div style={{ padding: '6px 16px 0' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1C2D41', marginBottom: 8 }}>
                3 ракурса — обязательно
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <PoseFigure label="Спереди" view="front" />
                <PoseFigure label="Сбоку" view="side" />
                <PoseFigure label="Сзади" view="back" />
              </div>
            </div>

            {/* Tips block */}
            <div style={{
              margin: '14px 16px 0', padding: 12,
              background: 'rgba(108,92,231,0.06)',
              border: '1px solid rgba(108,92,231,0.16)',
              borderRadius: 14,
              display: 'flex', gap: 10,
            }}>
              <Lightbulb size={18} color="#6C5CE7" style={{ flexShrink: 0, marginTop: 1 }} />
              <p style={{ fontSize: 12, color: '#1C2D41', margin: 0, lineHeight: 1.5 }}>
                Запомни ракурс, освещение и расстояние — каждый месяц снимай одинаково, иначе сравнивать прогресс будет невозможно.
              </p>
            </div>

            {/* Rules */}
            <div style={{ padding: '14px 16px 0' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1C2D41', marginBottom: 8 }}>
                Как правильно снимать
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {RULES.map((r, i) => (
                  <div key={i} style={{
                    display: 'flex', gap: 10,
                    background: 'white',
                    borderRadius: 12, padding: '10px 12px',
                    border: '1px solid rgba(26,58,92,0.06)',
                  }}>
                    <span style={{ fontSize: 18, lineHeight: '22px', flexShrink: 0 }}>{r.icon}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#1C2D41', marginBottom: 2 }}>{r.title}</div>
                      <div style={{ fontSize: 12, color: '#6B7B8D', lineHeight: 1.45 }}>{r.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* How submission works */}
            <div style={{
              margin: '14px 16px 0', padding: 12,
              background: 'rgba(36,161,222,0.06)',
              border: '1px solid rgba(36,161,222,0.20)',
              borderRadius: 14,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#1C2D41', marginBottom: 6 }}>
                Как отправить отчёт
              </div>
              <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: '#1C2D41', lineHeight: 1.6 }}>
                <li>Открой чат с куратором кнопкой ниже</li>
                <li>Прикрепи 3 фото (спереди / сбоку / сзади)</li>
                <li>Бот спросит: «Это фото-отчёт?» — нажми <b>Да</b></li>
                <li>Отчёт автоматически зачтётся, дата следующего откроется через 30 дней</li>
              </ol>
            </div>

            {/* CTA buttons */}
            <div style={{ padding: '16px 16px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                data-testid="photo-report-open-chat"
                onClick={handleOpenChat}
                disabled={!curatorChatUrl}
                style={{
                  width: '100%', height: 52, borderRadius: 16, border: 'none',
                  background: curatorChatUrl
                    ? 'linear-gradient(135deg, #229ED9 0%, #4A9FE5 100%)'
                    : 'rgba(26,58,92,0.1)',
                  color: curatorChatUrl ? 'white' : 'rgba(26,58,92,0.3)',
                  fontSize: 16, fontWeight: 700,
                  cursor: curatorChatUrl ? 'pointer' : 'default',
                  fontFamily: F,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  boxShadow: curatorChatUrl ? '0 8px 22px rgba(34,158,217,0.32)' : 'none',
                }}
              >
                <MessageCircle size={18} /> Сдать фото в Telegram
              </button>
              <button
                data-testid="photo-report-history"
                onClick={handleOpenHistory}
                style={{
                  width: '100%', height: 44, borderRadius: 14, border: 'none',
                  background: 'rgba(26,58,92,0.06)',
                  color: '#1A3A5C', fontSize: 14, fontWeight: 600,
                  cursor: 'pointer', fontFamily: F,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}
              >
                <ImageIcon size={16} /> Мои предыдущие фото
              </button>
            </div>
            </div>{/* /scrollable body */}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
